import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Settings, Search, Clock, Star, Globe } from 'lucide-react';
import { apiService, Service, ServiceCategory } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useToast } from '../contexts/ToastContext';
import SafeJoditEditor from '../components/SafeJoditEditor';
import { editorConfig } from '../utils/joditConfig';

const Services: React.FC = () => {
  const { showToast } = useToast();
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [detailedDescription, setDetailedDescription] = useState('');
  const [activeTab, setActiveTab] = useState<'content' | 'seo'>('content');
  const editor = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [serviceData, categoryData] = await Promise.all([
        apiService.getServices(),
        apiService.getServiceCategories()
      ]);
      setServices(serviceData || []);
      setCategories(categoryData || []);
    } catch (error) {
      console.error('Failed to fetch services/categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    fd.set('detailed_description', detailedDescription);

    // Normalize booleans
    ['is_featured', 'is_active'].forEach(field => {
      if (fd.has(field)) {
        const val = fd.get(field);
        fd.set(field, (val === 'on' || val === 'true') ? 'true' : 'false');
      } else {
        fd.append(field, 'false');
      }
    });

    // Handle empty files during PATCH to prevent validation errors
    if (editingService) {
      const featuredImage = fd.get('featured_image') as File;
      if (featuredImage && featuredImage.size === 0) {
        fd.delete('featured_image');
      }
      const ogImage = fd.get('og_image') as File;
      if (ogImage && ogImage.size === 0) {
        fd.delete('og_image');
      }
    }

    // Strictly parse schema_markup and remove if invalid or empty
    const schemaMarkup = fd.get('schema_markup');
    if (schemaMarkup && typeof schemaMarkup === 'string' && schemaMarkup.trim()) {
      try {
        JSON.parse(schemaMarkup);
      } catch (e) {
        console.error('Invalid JSON in schema_markup');
        fd.delete('schema_markup');
      }
    } else {
      fd.delete('schema_markup');
    }

    // ROBUSTNESS: Remove empty strings from optional fields to prevent 500 errors
    const optionalFields = ['meta_title', 'meta_description', 'meta_keywords', 'canonical_url', 'robots_meta', 'og_title', 'og_description', 'focus_keyword', 'price_range', 'duration'];
    optionalFields.forEach(field => {
      const val = fd.get(field);
      if (typeof val === 'string' && !val.trim()) {
        fd.delete(field);
      }
    });

    try {
      if (editingService) {
        await apiService.updateService(editingService.slug, fd);
      } else {
        await apiService.createService(fd);
      }
      setShowForm(false);
      showToast('Service saved successfully!', 'success');
      setEditingService(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save service:', error);
      showToast('Failed to save service. (Server Error)', 'error');
    }
  };

  const handleDelete = async (slug: string) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await apiService.deleteService(slug);
      showToast('Service deleted successfully!', 'success');
      fetchData();
    } catch (error) {
      showToast('Error deleting service.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Services</h1>
          <p className="text-gray-600">Manage your electrical services catalog and pricing</p>
        </div>
        <button
          onClick={() => {
            setEditingService(null);
            setDetailedDescription('');
            setActiveTab('content');
            setShowForm(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {editingService ? `Edit: ${editingService.title}` : 'New Service'}
                </h3>
                <div className="flex mt-2 space-x-6">
                  <button type="button" onClick={() => setActiveTab('content')} className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition ${activeTab === 'content' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>Content</button>
                  <button type="button" onClick={() => setActiveTab('seo')} className={`text-xs font-bold uppercase tracking-widest pb-1 border-b-2 transition ${activeTab === 'seo' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>SEO & Open Graph</button>
                </div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto">
              {activeTab === 'content' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Service Title</label>
                        <input name="title" defaultValue={editingService?.title} required className="w-full border border-gray-200 rounded-lg px-4 py-2 font-bold" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select name="category" defaultValue={editingService?.category} required className="w-full border border-gray-200 rounded-lg px-4 py-2">
                            <option value="">Select category</option>
                            {categories.map(cat => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                          <input name="duration" defaultValue={editingService?.duration} className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="e.g. 2-4 hours" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
                          <input name="price_range" defaultValue={editingService?.price_range} className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="e.g. NRS 50 - NRS 200" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image</label>
                          <input type="file" name="featured_image" accept="image/*" required={!editingService} className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                          {editingService?.featured_image && (
                            <div className="mt-2 h-16 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                              <img src={getImageUrl(editingService.featured_image) || undefined} alt="" className="h-full w-full object-cover" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                        <textarea name="short_description" defaultValue={editingService?.short_description} required rows={2} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="bg-gray-50 p-4 rounded-xl space-y-4">
                        <div className="flex items-center gap-2">
                          <input type="checkbox" name="is_featured" id="svc_featured" defaultChecked={editingService?.is_featured} className="h-5 w-5 text-orange-600 rounded" />
                          <label htmlFor="svc_featured" className="text-sm font-medium text-gray-700">Display on Homepage (Featured)</label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" name="is_active" id="svc_active" defaultChecked={editingService?.is_active !== false} className="h-5 w-5 text-orange-600 rounded" />
                          <label htmlFor="svc_active" className="text-sm font-medium text-gray-700">Service Active / Published</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Description</label>
                    <SafeJoditEditor
                      ref={editor}
                      value={detailedDescription}
                      config={editorConfig}
                      onBlur={newContent => setDetailedDescription(newContent)}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center"><Search className="h-3 w-3 mr-1" /> Search Optimization</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                      <input name="meta_title" defaultValue={editingService?.meta_title} className="w-full border border-gray-200 rounded-lg px-4 py-2" maxLength={60} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta Keywords</label>
                      <input name="meta_keywords" defaultValue={editingService?.meta_keywords} className="w-full border border-gray-200 rounded-lg px-4 py-2" placeholder="comma, separated" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                      <textarea name="meta_description" defaultValue={editingService?.meta_description} className="w-full border border-gray-200 rounded-lg px-4 py-2" rows={3} maxLength={160} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Robots Meta</label>
                        <input name="robots_meta" defaultValue={editingService?.robots_meta} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" placeholder="index, follow" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Focus Keyword</label>
                        <input name="focus_keyword" defaultValue={editingService?.focus_keyword} className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center"><Globe className="h-3 w-3 mr-1" /> Social Sharing (OpenGraph)</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">OG Title</label>
                      <input name="og_title" defaultValue={editingService?.og_title} className="w-full border border-gray-200 rounded-lg px-4 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">OG Description</label>
                      <textarea name="og_description" defaultValue={editingService?.og_description} className="w-full border border-gray-200 rounded-lg px-4 py-2" rows={2} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-xs">OG Image (1200x630)</label>
                      <input type="file" name="og_image" accept="image/*" className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100 file:text-gray-700" />
                      {editingService?.og_image && (
                        <div className="mt-2 h-16 w-32 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                          <img src={getImageUrl(editingService.og_image) || undefined} alt="" className="h-full w-full object-cover" />
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Schema Markup (JSON)</label>
                      <textarea name="schema_markup" defaultValue={typeof editingService?.schema_markup === 'object' ? JSON.stringify(editingService.schema_markup, null, 2) : editingService?.schema_markup} className="w-full border border-gray-200 rounded-lg px-4 py-2 font-mono text-xs" rows={3} />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2 text-gray-600 font-medium hover:text-gray-800 transition">Cancel</button>
                <button type="submit" className="px-10 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition shadow-lg shadow-orange-100">
                  {editingService ? 'Update Service' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <div key={service.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition group">
            <div className="relative h-48 bg-gray-100">
              {service.featured_image && (
                <img src={getImageUrl(service.featured_image) || undefined} alt={service.title} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={async () => {
                    try {
                      const detail = await apiService.getServiceDetail(service.slug);
                      setEditingService(detail);
                      setDetailedDescription(detail.detailed_description || '');
                      setShowForm(true);
                    } catch (err) {
                      console.error('Failed to fetch service detail', err);
                    }
                  }}
                  className="p-2 bg-white rounded shadow text-orange-600 hover:bg-orange-50"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(service.slug)} className="p-2 bg-white rounded shadow text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-[10px] font-bold uppercase">
                  {service.category_name}
                </span>
                {service.is_featured && <Star className="h-4 w-4 text-yellow-500 fill-current" />}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 truncate">{service.title}</h3>
              <p className="text-gray-500 text-sm mb-4 line-clamp-2">{service.short_description}</p>

              <div className="flex items-center gap-4 border-t pt-3 text-xs text-gray-400">
                <div className="flex items-center">
                  NRS {service.price_range || 'N/A'}
                </div>
                <div className="flex items-center">
                  <Clock className="h-3 w-3 mr-1" /> {service.duration || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {services.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-xl border border-gray-100">
          <Settings className="mx-auto h-10 w-10 text-gray-300 mb-3" />
          <p className="text-gray-500">No services found. Click "Add Service" to start.</p>
        </div>
      )}
    </div>
  );
};

export default Services;